/**
 * Montagem do painel: junta as regras puras na ordem em que dependem umas
 * das outras, para a tela (e o hook) chamarem uma função só.
 */
import { fimDoMes, somarMeses } from "./calendario";
import { SITUACAO, classificarLinhas, unirTerminos } from "./classificacao";
import { REGUA, calcularSegundaLeitura } from "./excecoes";
import { ehDaPlanilha, projetarFila } from "./fila";
import { montarProjecaoParaFoto } from "./foto";
import { QUANTIDADE_PADRAO, ESCALA } from "./periodos";
import { medirPrecisao } from "./precisao";
import { avaliarQualidade } from "./qualidade";
import { calcularRitmo } from "./ritmo";
import { itensDeVendasAssinadas, vendasAguardando } from "./vendasAssinadas";

/** Último dia que a tela projeta por padrão: fim do 5º mês seguinte. */
export function horizontePadrao(hoje) {
  return fimDoMes(somarMeses(hoje, QUANTIDADE_PADRAO[ESCALA.MES].seguintes));
}

/**
 * `calcularPainelCapacidade({extrato, terminosBackend, fotos, naoUteis,
 * anosComRegistro, hoje, horizonteAte?, marcas?, vendasAssinadas?})` →
 * `{ok, motivo, classificacao, abertos, terminos, ritmo, precisao, fila,
 * filaContagem, segundaLeitura, regua, qualidade, projecaoFoto, vendasAssinadas}`.
 *
 * `vendasAssinadas` = `VENDA`s do backend; as `aguardando` entram em
 * `abertos` (e daí na fila, na simulação e no cenário) como camada
 * `venda_assinada`. A foto e a guarda de "incompleto" contam só a planilha:
 * a precisão compara a fila da foto com os términos da planilha, e a
 * qualidade só fala do que o PCP digitou.
 *
 * `marcas` = exceções de carga do PCP (`{chave: {classe, ...}}`). `fila` é a
 * projeção da régua oficial: a contagem, ou a leitura com pesos quando as
 * marcas já cobrem o histórico (`excecoes.js`). A foto guardada e a
 * qualidade usam sempre a contagem (`filaContagem`): é nela que a precisão é
 * medida e o limite de "incompleto" foi definido.
 *
 * Extrato não ok (layout): `ok:false` e só a `qualidade` (com o item
 * "layout"), que diz o que falta na planilha.
 */
export function calcularPainelCapacidade({
  extrato,
  terminosBackend,
  fotos,
  naoUteis,
  anosComRegistro,
  hoje,
  horizonteAte = horizontePadrao(hoje),
  marcas = {},
  vendasAssinadas = [],
} = {}) {
  if (!extrato?.ok) {
    return {
      ok: false,
      motivo: extrato?.motivo || "sem_extrato",
      qualidade: avaliarQualidade({ extrato, hoje, naoUteis }),
    };
  }
  const classificacao = classificarLinhas(extrato.linhas, hoje);
  const aguardando = vendasAguardando(vendasAssinadas);
  const abertos = [
    ...classificacao.linhas.filter((linha) => linha.situacao === SITUACAO.ABERTO),
    ...itensDeVendasAssinadas(aguardando),
  ];
  const terminos = unirTerminos(terminosBackend, classificacao.linhas, hoje);
  const ritmo = calcularRitmo({ terminos, naoUteis, hoje, anosComRegistro, horizonteAte });
  const precisao = medirPrecisao({ fotos, terminos, naoUteis, hoje });
  const filaContagem = projetarFila({ abertos, ritmo: ritmo.ritmo, naoUteis, hoje, precisao });
  const segundaLeitura = calcularSegundaLeitura({ abertos, terminos, ritmo, marcas, naoUteis, hoje, precisao });
  const oficialPonderada = Boolean(segundaLeitura.disponivel && segundaLeitura.oficial);
  const filaDaPlanilha = { itens: filaContagem.itens.filter(ehDaPlanilha) };
  const qualidade = avaliarQualidade({ extrato, classificacao, ritmo, fila: filaDaPlanilha, naoUteis, hoje });
  return {
    ok: true,
    motivo: ritmo.motivo,
    classificacao,
    abertos,
    terminos,
    ritmo,
    precisao,
    fila: oficialPonderada ? segundaLeitura.projecao : filaContagem,
    filaContagem,
    segundaLeitura,
    regua: oficialPonderada ? REGUA.PONDERADA : REGUA.CONTAGEM,
    qualidade,
    projecaoFoto: montarProjecaoParaFoto({ hoje, fila: filaDaPlanilha.itens.length, ritmo }),
    vendasAssinadas: aguardando,
  };
}
